import { SelectionModel } from '@angular/cdk/collections';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { EventService, Version, VersionService, Event, OfferPromoService, OfferPromo, PromoDomain } from '../..';
import * as dayjs from 'dayjs';

const log = new Logger('VersionListComponent');

@Component({
  selector: 'app-version-list',
  templateUrl: './version-list.component.html',
  styleUrls: ['./version-list.component.scss']
})

export class VersionListComponent<TVersion extends Version, TEvent extends Event, TOfferPromo extends OfferPromo, TPromoDomain extends PromoDomain<OfferPromo>> implements OnInit {

  viewModel$ = combineLatest([
    this.versionService.versionDialogVersions$,
    this.versionService.isLoading$,
    this.versionService.versionDialogTotalRecords$,
    this.versionService.versionDialogPage$,
    this.eventService.getEvent(this.route.snapshot.parent?.parent?.params['eventId'] || this.versionService.eventId)
  ]).pipe(
    map(([versions, isLoading, totalRecords, page, event]) => {
      if (versions.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.versionService.page(page);
        this.versionService.reload();
      }
      return { versions, isLoading, totalRecords, page, event }
    }),
  );

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'VersionName', 'ClientKey', 'Actions'];
  versionList: Version[] = [];
  eventId!: string;
  selection = new SelectionModel<Version>(true, []);
  mode: 'add' | 'remove' = 'add'
  selectedOffers: any

  constructor(
    private eventService: EventService<TEvent>,
    private versionService: VersionService<TVersion>,
    private mediaObserver: MediaObserver,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private matSnackBar: MatSnackBar) {
    if (this.dialogData && this.dialogData.mode) {
      this.mode = this.dialogData.mode;
      this.selectedOffers = this.dialogData.selectedRecordsDetails
    }
  }

  ngOnInit(): void {
    log.debug('init');
    // get the event id from the parent route
    this.route.params.subscribe(params => {
      this.eventId = params['eventId'];
    });

    if (this.dialogData && this.dialogData.mode) {
      this.mode = this.dialogData.mode;
      this.selectedOffers = this.dialogData.selectedRecordsDetails
    }

    this.versionService.versionDialogVersions$.subscribe((data) => {
      this.versionList = data;
    })
    this.clearSearch();
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'VersionName', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'VersionName', 'ClientKey', 'Actions'];
        }
        if (this.dialogData) {
          this.displayedColumns.pop();
        }
      });

    // get and set the event id in the version service
    if (this.route.snapshot.parent && this.route.snapshot.parent.parent) {
      //this.versionService.eventId = this.route.snapshot.parent.params['eventId'];
      this.versionService.eventId = this.route.snapshot.parent.parent.params['eventId'];
    }
  }

  filtersForm = new FormGroup({
    search: new FormControl(),
  });

  onSearch(event: any) {
    this.clearSelection();
    this.versionService.versionDialogSearch(event.target.value);
  }

  onPage(pageEvent: PageEvent): void {
    this.clearSelection();
    this.versionService.versionDialogPage(pageEvent);
  }

  clearSearch() {
    this.clearSelection();
    this.filtersForm.controls.search.setValue('');
    this.versionService.versionDialogSearch('');
  }

  onSort(sortState: Sort): void {
    this.versionService.versionDialogSort(sortState);
    this.clearSelection();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.versionList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.clearSelection() :
      this.versionList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  onRowDoubleClick(id: string) {
    this.router.navigate([`${id}/details`], { relativeTo: this.route });
  }

  deleteSelectedRecords() {
    let selectedRecords: any;
    selectedRecords = this.getSelectedSectionRecords();
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Version(s)?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            this.versionService.deleteVersions(selectedRecords, null).subscribe({
              next: () => {
                this.matSnackBar.open(`Version(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                this.versionService.reload();
                this.clearSelection();
              },
              error: (error) => {
                log.error('Error in deleting Version', error);

                if (error.error.value) {
                  throw new Error(error.error.value);
                } else {
                  throw new Error(error.message);
                }
              }
            });
          }
        });
    }
  }

  downloadVersions() {
    this.versionService.downloadVersions(this.eventId);
  }

  downloadVersionStores() {
    this.versionService.downloadVersionStores(this.eventId);
  }

  selectVersions() {
    if (this.mode !== 'add') return;
    let addVersionCodes: any = [];
    for (let index = 0; index < this.dialogData.selectedRecordsDetails.length; index++) {
      const record = this.dialogData.selectedRecordsDetails[index];
      const x = record.Variants?.find((x: any) => x.activeItem) ?? record.Detail;

      if (!x) continue;

      addVersionCodes.push({
        Id: x.Id,
        ClientKey: x.ClientKey,
        UserId: x.UserId,
        Name: x.Name,
        Headline: x.Headline,
        BodyCopy: x.BodyCopy,
        Disclaimer: x.Disclaimer,
        ClientComment: x.ClientComment,
        StartDate: dayjs(x.StartDate).format('MM/DD/YYYY'),
        EndDate: dayjs(x.EndDate).format('MM/DD/YYYY'),
        Rank: x.Rank,
        TargetURL: x.TargetURL,
        Status: x.Status,
        Version: x.Version || record?.VersionNames || null,
        Label: x.Label || record?.EventOfferType || '',
        Variants: x.Variants ?? record?.Variants ?? []
      });
    }

    this.versionService.applyVersionCodes(this.selection.selected, addVersionCodes).subscribe(res => {
      this.matSnackBar.open(`Applied versions to all offers and promos`, 'OK', DEFAULT_SNACKBAR_CONFIG);
      this.dialog.closeAll();
      this.clearSelection();
      this.offerpromoService.reload();
    })
  }

  disAssociateVersionsByOffers() {
    if (this.mode !== 'remove') return;
    const offerKeys: string[] = (this.selectedOffers ?? []).flatMap(
      (r: any) =>
        r.Variants?.length ? r.Variants.map((v: any) => String(v.Id)) : [String(r.Detail?.Id ?? r.DomainId)]
    );

    // validate
    if (offerKeys.length === 0) {
      this.matSnackBar.open('No offers available to remove versions from.', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    if (!this.selection || this.selection.selected.length === 0) {
      this.matSnackBar.open('Please select version(s) to remove.', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    const eventId = this.route.snapshot.parent?.parent?.params['eventId'] || this.versionService.eventId;

    this.versionService.disAssociateVersionsByOffers(this.selection.selected, offerKeys, eventId)
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          this.clearSelection();
          this.offerpromoService.reload();
          this.matSnackBar.open('Versions successfully removed from selected offers.', 'OK', DEFAULT_SNACKBAR_CONFIG);

          if (this.versionService.clearVersionDialogKeys) {
            this.versionService.clearVersionDialogKeys();
          }
        },
        error: (err) => {
          log.error('Error disassociating versions', err);
          const msg = err?.error?.value || err?.message || 'Error removing versions from offers';
          this.matSnackBar.open(msg, 'OK', DEFAULT_SNACKBAR_CONFIG);
        },
        complete: () => {
        }
      });
  }


  cancel() {
    this.dialog.closeAll();
  }

  ngOnDestroy(): void {
    this.flexMediaWatcher.unsubscribe();
  }

}
